import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { menu, routeForSlug, isPageBuilt, filterMenuForRoles } from '../../config/menuData.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { roleKeysOf } from '../../config/roles.js';
import { useLocale } from '../../context/LocaleContext.jsx';
import { useFeatures, pruneMenuForFeatures } from '../../services/features.js';

/**
 * The layouts' navigation, driven entirely by src/config/menuData.js — the
 * reference file generated from the `menuData` variable the raw layout shells
 * declare. Nothing here is hard-coded: labels, icons, grouping and order all
 * come from that file, and every item routes by **slug**, never by page id.
 *
 * The raw shells rely on the design preview's menu-engine.js, which builds a
 * Bootstrap navbar imperatively and navigates by posting a WORKFLOW_MENU_NAVIGATION
 * message to the parent frame. Here the same Bootstrap 5 markup (nav-item /
 * nav-link / dropdown / dropdown-menu / dropdown-item) is produced by React and
 * navigation goes through the router instead. Dropdown open/closed state is owned
 * by React rather than Bootstrap's JS so re-renders and route changes can't leave
 * a stale popover behind.
 *
 * An entry whose page component has not been generated yet resolves to the 404
 * placeholder (see routeForSlug) and is dimmed — the nav never dead-ends.
 */

/** Matches the design menu engine's `sanitizeId()` so element ids line up. */
function sanitizeId(label) {
  return label.replace(/\W+/g, '_');
}

/**
 * Catalogue key for a menu label — `Asset Vault & Projects` → `asset_vault_projects`.
 * menuData.js stays the source of menu STRUCTURE; the words come from the
 * locale catalogue, so switching to Arabic translates the chrome rather than
 * only flipping its direction.
 */
export function navKeyFor(label) {
  return `nav.${String(label)
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')}`;
}

/**
 * Translated label, falling back to the English label in menuData.js. `t()`
 * returns the key itself when absent, which is the signal we fall back on — a
 * new menu entry shows readable English rather than a raw `nav.foo` key.
 */
export function useNavLabel(t) {
  return (label) => {
    const key = navKeyFor(label);
    const translated = t(key);
    return translated === key ? label : translated;
  };
}

function MenuIcon({ icon }) {
  if (!icon) return null;
  return <i className={`${icon} app-nav__icon me-2`} aria-hidden="true" />;
}

function MenuLeaf({ item, level, onNavigate, label }) {
  const route = routeForSlug(item.slug);
  const built = isPageBuilt(item.slug);

  const base = level === 0 ? 'nav-link app-nav__link' : 'dropdown-item';

  return (
    <li className={level === 0 ? 'nav-item' : undefined}>
      <NavLink
        to={route}
        end
        className={({ isActive }) =>
          [base, isActive ? 'active' : '', built ? '' : 'app-nav__link--unbuilt']
            .filter(Boolean)
            .join(' ')
        }
        title={built ? undefined : `${label ?? item.label} — page not generated yet`}
        aria-disabled={built ? undefined : 'true'}
        data-slug={item.slug ?? undefined}
        data-page-id={item.pageId ?? undefined}
        data-module-id={item.moduleId ?? undefined}
        onClick={onNavigate}
      >
        <MenuIcon icon={item.icon} />
        {label ?? item.label}
      </NavLink>
    </li>
  );
}

function MenuGroup({ item, level, onNavigate, label, navLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const toggleId = `mainMenu-${sanitizeId(item.label)}`;

  // Close on route change so a dropdown never survives a navigation.
  useEffect(() => setOpen(false), [location.pathname]);

  // Close on outside click / Escape — the behaviour Bootstrap's dropdown JS
  // would provide, kept in React so it stays in sync with the rendered state.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Bootstrap 5's .nav-link / .dropdown-item both reset <button> chrome, so the
  // toggle can be a real button (keyboard-operable) while keeping the design's
  // classes.
  const toggleClass =
    level === 0 ? 'nav-link app-nav__link dropdown-toggle' : 'dropdown-item dropdown-toggle';

  return (
    <li className={`${level === 0 ? 'nav-item ' : ''}dropdown`} ref={ref}>
      <button
        type="button"
        id={toggleId}
        className={`${toggleClass} text-start`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <MenuIcon icon={item.icon} />
        {label ?? item.label}
      </button>
      <ul
        className={`dropdown-menu${open ? ' show' : ''}`}
        aria-labelledby={toggleId}
        data-menu-level={level + 1}
      >
        {item.children.map((child) => (
          <MenuNode key={child.label} item={child} level={level + 1} onNavigate={onNavigate} navLabel={navLabel} />
        ))}
      </ul>
    </li>
  );
}

function MenuNode({ item, level, onNavigate, navLabel }) {
  return item.children && item.children.length > 0 ? (
    <MenuGroup item={item} level={level} onNavigate={onNavigate} label={navLabel(item.label)} navLabel={navLabel} />
  ) : (
    <MenuLeaf item={item} level={level} onNavigate={onNavigate} label={navLabel(item.label)} />
  );
}

/**
 * `#navbarNav`'s contents — the `<ul class="navbar-nav">` the design's
 * renderWebMenu() injects, rendered declaratively.
 */
export default function NavMenu({ items = menu.children, onNavigate }) {
  const { user } = useAuth();
  const { t } = useLocale();
  const navLabel = useNavLabel(t);
  const roleKeys = useMemo(() => roleKeysOf(user), [user]);
  // Role-gated entries (e.g. Compliance & Audit — Auditor/Compliance +
  // Platform Administrator only) are dropped entirely for a user who holds
  // none of their `requiredRoles`; unrestricted entries render as before.
  // Feature-flagged entries (Talking Avatar behind FEATURE_TALKING_AVATAR)
  // are pruned after role filtering; the flag only ever hides, default on.
  const features = useFeatures();
  const visibleItems = useMemo(
    () => pruneMenuForFeatures(filterMenuForRoles(items, roleKeys), features),
    [items, roleKeys, features],
  );

  return (
    <ul className="navbar-nav">
      {visibleItems.map((item) => (
        <MenuNode key={item.label} item={item} level={0} onNavigate={onNavigate} navLabel={navLabel} />
      ))}
    </ul>
  );
}
